import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { CheckCircle2, Calendar, AlertTriangle, FileText, Building2, CreditCard, Package } from "lucide-react";

/**
 * Il mio contratto (Client-side).
 *
 * NON mostra un listino pubblico. Il cliente vede SOLO:
 * - Attività scelta (Negozio Animali / Toelettatura / Entrambe)
 * - Moduli attivi (etichette leggibili)
 * - Importo pattuito (concordato nel preventivo iniziale)
 * - Ciclo di fatturazione (mensile/annuale) + prossima scadenza
 * - Metodo di pagamento + stato
 * - Banner promemoria scadenza se ≤ 15 giorni
 *
 * Nessun altro prezzo, nessun upsell, nessun listino comparativo.
 */
export default function Billing() {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my-contract").then(r => setC(r.data)).catch(() => setC(null)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-velta-mute">Caricamento…</div>;
  }
  if (!c) {
    return (
      <div className="p-8 lg:p-12 max-w-3xl">
        <h1 className="font-heading text-3xl font-bold mb-2">Il mio contratto</h1>
        <p className="text-velta-mute">Nessun contratto ancora attivo. Contatta VELTA SISTEMI per iniziare.</p>
      </div>
    );
  }

  const isActive = c.contract_status === "attivo";
  const statusColors = {
    attivo: { bg: "bg-velta-success/5", border: "border-velta-success", text: "text-velta-success", label: "Contratto attivo" },
    bozza: { bg: "bg-yellow-50", border: "border-yellow-400", text: "text-yellow-700", label: "In attesa di attivazione" },
    sospeso: { bg: "bg-orange-50", border: "border-orange-400", text: "text-orange-700", label: "Contratto sospeso" },
    scaduto: { bg: "bg-red-50", border: "border-red-400", text: "text-red-700", label: "Contratto scaduto" },
  }[c.contract_status] || { bg: "bg-velta-bg", border: "border-velta-border", text: "text-velta-mute", label: c.contract_status };

  const paymentColors = {
    pagato: "text-velta-success",
    in_ritardo: "text-velta-error",
    pending: "text-yellow-700",
  };
  const paymentLabels = {
    pagato: "Pagato",
    in_ritardo: "In ritardo",
    pending: "In attesa",
  };
  const methodLabels = {
    bonifico: "Bonifico bancario",
    riba: "RiBa",
    carta: "Carta",
    contanti: "Contanti",
    altro: "Altro",
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Il mio contratto</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Contratto e rinnovo</h1>
        <p className="text-velta-mute">
          Qui trovi i termini del contratto sottoscritto con VELTA SISTEMI. Per modificarli, contatta il tuo referente.
        </p>
      </div>

      {/* Banner scadenza (se ≤ 15 giorni) */}
      {c.renewal_banner && c.next_due_date && (
        <div
          data-testid="contract-renewal-banner"
          className="mb-6 border-2 border-yellow-400 bg-yellow-50 rounded-md p-5 flex items-start gap-4"
        >
          <AlertTriangle className="w-6 h-6 text-yellow-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-bold text-yellow-900 text-lg mb-1">
              Il tuo canone scade tra {c.days_to_renewal} {c.days_to_renewal === 1 ? "giorno" : "giorni"}
            </div>
            <div className="text-sm text-yellow-900">
              Scadenza: <strong>{new Date(c.next_due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</strong> ·
              Importo: <strong className="font-mono">{c.agreed_amount_eur != null ? formatEUR(c.agreed_amount_eur) : "—"}</strong>
              {c.payment_method && <> · Metodo: <strong>{methodLabels[c.payment_method]}</strong></>}
            </div>
            <div className="text-xs text-yellow-800 mt-2">
              Ricorda di procedere al pagamento entro la scadenza per mantenere attivi tutti i servizi.
            </div>
          </div>
        </div>
      )}

      {/* Card contratto */}
      <div className={`rounded-md border-2 ${statusColors.border} ${statusColors.bg} p-6 mb-6`} data-testid="contract-status-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className={`w-7 h-7 ${statusColors.text}`} />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Stato</div>
              <div data-testid="contract-status-label" className={`font-heading text-xl font-bold ${statusColors.text}`}>{statusColors.label}</div>
            </div>
          </div>
          {isActive && c.activation_date && (
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Attivato dal</div>
              <div className="font-mono">{new Date(c.activation_date).toLocaleDateString("it-IT")}</div>
            </div>
          )}
        </div>
      </div>

      {/* Griglia dati */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* Attività + piano */}
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="flex items-center gap-2 mb-2 text-velta-mute">
            <Building2 className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-widest">Attività</span>
          </div>
          <div className="font-heading text-2xl font-bold" data-testid="contract-activity">
            {c.activity_label || "—"}
          </div>
          {c.billing_cycle && (
            <div className="text-sm text-velta-mute mt-1">
              <strong className="text-velta-ink">{c.billing_cycle === "mensile" ? "Mensile" : "Annuale"}</strong>
            </div>
          )}
        </div>

        {/* Importo */}
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="flex items-center gap-2 mb-2 text-velta-mute">
            <CreditCard className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-widest">Importo pattuito</span>
          </div>
          <div className="font-mono font-bold text-3xl tracking-tighter" data-testid="contract-amount">
            {c.agreed_amount_eur != null ? formatEUR(c.agreed_amount_eur) : "—"}
          </div>
          <div className="text-sm text-velta-mute mt-1">
            {c.billing_cycle === "mensile" ? "al mese" : c.billing_cycle === "annuale" ? "all'anno" : "canone software"}
          </div>
        </div>

        {/* Prossima scadenza */}
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="flex items-center gap-2 mb-2 text-velta-mute">
            <Calendar className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-widest">Prossima scadenza</span>
          </div>
          <div className="font-heading text-xl font-bold" data-testid="contract-next-due">
            {c.next_due_date
              ? new Date(c.next_due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })
              : "—"}
          </div>
          {c.days_to_renewal != null && c.days_to_renewal >= 0 && (
            <div className="text-sm text-velta-mute mt-1">
              tra {c.days_to_renewal} {c.days_to_renewal === 1 ? "giorno" : "giorni"}
            </div>
          )}
        </div>

        {/* Metodo & stato pagamento */}
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="flex items-center gap-2 mb-2 text-velta-mute">
            <FileText className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-widest">Pagamento</span>
          </div>
          <div className="font-heading text-xl font-bold" data-testid="contract-payment-method">
            {methodLabels[c.payment_method] || "—"}
          </div>
          {c.payment_status && (
            <div className={`text-sm font-medium mt-1 ${paymentColors[c.payment_status] || "text-velta-mute"}`} data-testid="contract-payment-status">
              {paymentLabels[c.payment_status] || c.payment_status}
            </div>
          )}
        </div>
      </div>

      {/* Moduli attivi */}
      {c.active_modules_labels && c.active_modules_labels.length > 0 && (
        <div className="bg-white border border-velta-border rounded-md p-5" data-testid="contract-modules">
          <div className="flex items-center gap-2 mb-3">
            <Package className="w-4 h-4" />
            <h3 className="font-heading font-semibold">Moduli attivi nel tuo contratto</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {(Array.isArray(c.active_modules_labels) ? c.active_modules_labels : []).map((m, i) => (
              <div key={i} className="inline-flex items-center gap-1.5 bg-velta-ink/5 border border-velta-ink/20 rounded px-3 py-1.5 text-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-velta-success" /> {m}
              </div>
            ))}
          </div>
          <p className="text-xs text-velta-mute mt-3">
            Per aggiungere o rimuovere moduli, contatta il tuo referente VELTA SISTEMI.
          </p>
        </div>
      )}

      <div className="mt-6 text-xs text-velta-mute text-center">
        Hai bisogno di modificare qualcosa nel tuo contratto? Contatta il tuo referente VELTA SISTEMI.
      </div>
    </div>
  );
}
